import type { WorldSlug } from '@/lib/worlds';

// SI-Inspiration content lock 2026-05-11 (Sebastian Gate-1).
// Replaces the prior 6-item pool with an 11-item content set.
// All entries currently world=null; field is forward-compatible
// for future per-world filtering. Backend Phase 2J.4 InspirationTip
// endpoints remain orphan code per briefing line 765 — PWA does
// not consume them.

export interface Tip {
  /** Stable slug for React keys + tests. */
  id: string;
  /** Optional emoji rendered before the title in the card heading. */
  emoji?: string;
  /** Short noun-phrase headline. */
  title: string;
  /** Body copy — em-dashes preserved verbatim from prompt. */
  body: string;
  /**
   * Forward-compatible per-world tagging. All locked entries are
   * `null` at SI-Inspiration time (2026-05-11) — added so a future
   * filter-by-world pass can light up without a second type change.
   */
  world: WorldSlug | null;
  /** Optional CTA — kept on the type for forward-compat; all locked entries null. */
  ctaLabel: string | null;
  ctaTarget: string | null;
}

/**
 * Sebastian-locked tip pool. 11 entries, 3-day-rotated by
 * `getCurrentTip()` for the lead-tip on Start-Tab AND as the
 * lead of the Inspiration-Tab trio. Anker-Items (Sportstar +
 * Lieblings-Heldin) intentionally redundant with the equivalent
 * STORY_IDEAS entries for retention.
 */
export const TIPS: readonly Tip[] = [
  {
    id: 'familien-routine',
    emoji: '⏰',
    title: 'Familien-Routine',
    body: 'Lest jeden Abend zur gleichen Zeit eine Geschichte — Routine ist die beste Einschlafhilfe.',
    world: null,
    ctaLabel: null,
    ctaTarget: null,
  },
  {
    id: 'familie-freunde-helden',
    emoji: '👥',
    title: 'Familie & Freunde als Helden',
    body: 'Mama, Papa, Oma, Geschwister, beste Freunde — bau sie als Mitfiguren in die Geschichte ein. Macht es persönlicher und unvergesslicher.',
    world: null,
    ctaLabel: null,
    ctaTarget: null,
  },
  {
    id: 'vertraute-orte',
    emoji: '🏠',
    title: 'Vertraute Orte als Kulisse',
    body: 'Kita, Küche, Spielplatz, Lieblings-Park — euer Alltag wird zur Story-Kulisse. Je vertrauter der Ort, desto stärker zieht die Geschichte das Kind rein.',
    world: null,
    ctaLabel: null,
    ctaTarget: null,
  },
  {
    id: 'event-besuch',
    emoji: '🎉',
    title: 'Wenn Besuch da ist',
    body: 'Verwandte, Freunde oder andere Familien zu Besuch? Macht ein Geschichten-Event daraus: Wer darf das nächste Thema bestimmen — und wer von den Anwesenden soll in der Geschichte vorkommen?',
    world: null,
    ctaLabel: null,
    ctaTarget: null,
  },
  {
    id: 'event-familie',
    emoji: '🎊',
    title: 'Familien-Geschichten-Runde',
    body: 'Macht aus dem gemeinsamen Hören ein Ritual: heute bestimmt ein Kind das Thema, morgen ein Geschwister, übermorgen Mama oder Papa. Wer dabei ist, taucht in der Geschichte auf.',
    world: null,
    ctaLabel: null,
    ctaTarget: null,
  },
  {
    id: 'kuscheltier-rolle',
    emoji: '🧸',
    title: 'Kuscheltier mit eigener Rolle',
    body: 'Das Lieblings-Kuscheltier bekommt seine eigene Rolle in der Geschichte — als Sidekick, als Helfer, manchmal als heimliche Hauptperson.',
    world: null,
    ctaLabel: null,
    ctaTarget: null,
  },
  {
    id: 'lieblings-heldin',
    emoji: '👫',
    title: 'Lieblings-Heldin oder Lieblings-Held einbauen',
    body: 'Die Figur aus dem Lieblings-Buch oder der Lieblings-Serie darf in der Geschichte mit-Hauptrolle spielen. Macht es persönlich und sofort wiedererkennbar.',
    world: null,
    ctaLabel: null,
    ctaTarget: null,
  },
  {
    id: 'lieblings-sportstar',
    emoji: '⚽',
    title: 'Lieblings-Sportstar dabei',
    body: 'Profi vom Lieblingsverein, Lieblings-Trainerin, Vorbild aus dem Sport — dein Kind trifft sein Idol in der Geschichte und erlebt mit ihm einen gemeinsamen Moment.',
    world: null,
    ctaLabel: null,
    ctaTarget: null,
  },
  {
    id: 'ausfuehrlich-beschreiben',
    emoji: '✏️',
    title: 'Ausführlich beschreiben lohnt sich',
    body: 'Je mehr Details du im Prompt mitgibst (Stimmung, Ort, Personen, kleine Anlässe), desto präziser wird die Geschichte. Ein, zwei Sätze reichen — drei sind besser.',
    world: null,
    ctaLabel: null,
    ctaTarget: null,
  },
  {
    id: 'kinderprofil-vollstaendig',
    emoji: '👤',
    title: 'Kinderprofil vollständig ausfüllen',
    body: 'Kuscheltiere, Geschwister, Oma & Opa, beste Freunde, vertraute Orte — je vollständiger das Profil, desto personalisierter die Geschichten. Jederzeit anpassbar unter Account → Kinderprofil.',
    world: null,
    ctaLabel: null,
    ctaTarget: null,
  },
  {
    id: 'kind-neugier',
    emoji: '🔍',
    title: 'Was dein Kind gerade neugierig macht',
    body: 'Lieblingsfragen aus Kita, Spaziergang oder Abendrunde — werden bei Schlauberger zu Geschichten mit echten Fakten und Wow-Momenten.',
    world: null,
    ctaLabel: null,
    ctaTarget: null,
  },
] as const;

/**
 * Day index since the UTC epoch — exposed as a small testable
 * helper so rotation tests can mock a single seam rather than the
 * global Date constructor. Returns floor(now.ms / 86_400_000).
 */
function daysSinceEpoch(date: Date = new Date()): number {
  return Math.floor(date.getTime() / (1000 * 60 * 60 * 24));
}

/**
 * Lead tip of the current 3-day rolling window. Same calendar day
 * (UTC) → same tip across all users; index advances every 3 days.
 * Cadence chosen over weekly (Sebastian Gate-1 lock 2026-05-11) for
 * higher content variety in early post-launch weeks.
 *
 * Same tip displayed on Start-Tab AND as the LEAD of the
 * Inspiration-Tab trio — provides cross-surface continuity.
 */
export function getCurrentTip(date: Date = new Date()): Tip {
  const idx = Math.floor(daysSinceEpoch(date) / 3) % TIPS.length;
  return TIPS[idx]!;
}

/**
 * Rolling trio of tips for Inspiration-Tab. Lead = getCurrentTip();
 * slots 2 and 3 advance through the pool by +1 and +2 respectively
 * (wrapping). After 11 cycles (~33 days) every tip will have held
 * the lead position once.
 */
export function getCurrentTipTrio(date: Date = new Date()): readonly [Tip, Tip, Tip] {
  const leadIndex = Math.floor(daysSinceEpoch(date) / 3) % TIPS.length;
  return [
    TIPS[leadIndex]!,
    TIPS[(leadIndex + 1) % TIPS.length]!,
    TIPS[(leadIndex + 2) % TIPS.length]!,
  ] as const;
}

/**
 * The remaining TIPS.length-3 tips not in the current trio, in
 * natural pool order starting at trio-lead + 3. Used by the
 * Inspiration-Tab "Alle Tipps anzeigen" expander.
 */
export function getRemainingTips(date: Date = new Date()): readonly Tip[] {
  const leadIndex = Math.floor(daysSinceEpoch(date) / 3) % TIPS.length;
  return Array.from(
    { length: TIPS.length - 3 },
    (_, i) => TIPS[(leadIndex + 3 + i) % TIPS.length]!,
  );
}
